import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

export interface Segment<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  /** Overrides the selected-state styling (e.g. warning tone for Production). */
  selectedSx?: SxProps<Theme>;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: ReadonlyArray<Segment<T>>;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'small' | 'medium' | 'large';
}

const HEIGHT = { small: 28, medium: 32, large: 36 } as const;

const trackBg = (theme: Theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.05 : 0.1);

/** Single-select pill group (radiogroup semantics). */
function SegmentedControl<T extends string>({ value, options, onChange, size = 'medium', ...aria }: SegmentedControlProps<T>) {
  const large = size === 'large';
  return (
    <Box
      role="radiogroup"
      aria-label={aria['aria-label']}
      sx={{ display: 'inline-flex', flexShrink: 0, bgcolor: trackBg, gap: large ? 0.5 : 0.25, p: large ? 0.5 : 0.375, borderRadius: large ? 6 : 2 }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            sx={[
              {
                height: HEIGHT[size],
                minWidth: 40,
                pl: o.icon ? (large ? 1.5 : 1) : large ? 2 : 1.25,
                pr: large ? 2 : 1.25,
                gap: 0.75,
                borderRadius: large ? 4.5 : 1.5,
                fontSize: size === 'small' ? 12 : 13,
                fontWeight: 600,
                color: 'text.secondary',
                '& svg': { fontSize: 18 },
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
              },
              on && { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,21,41,.12)' },
              ...(on && o.selectedSx ? [o.selectedSx] : []),
            ] as SxProps<Theme>}
          >
            {o.icon}
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export default SegmentedControl;
